import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { 
  Palmtree, 
  Stethoscope, 
  Calendar, 
  Clock, 
  Plus, 
  Loader2, 
  ChevronLeft, 
  ChevronRight,
  BarChart3,
} from "lucide-react";
import Modal from "../../../components/admin/Modal";
import { useToast } from "../../../components/admin/ToastContext";
import { get, post } from "../../../services/lewooApi";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

const STATE_MAP = {
  draft: "pending",
  confirm: "pending",
  validate: "approved",
  validate1: "approved",
  validate2: "approved",
  DRAFT: "pending",
  PENDING: "pending",
  APPROVED_L1: "approved",
  APPROVED: "approved",
  REFUSED: "refused",
  CANCELLED: "refused",
};
const STATE_STYLE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  pending: "bg-amber-50 text-amber-700 border-amber-100",
  refused: "bg-rose-50 text-rose-700 border-rose-100",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("fr");
}

function idVal(x) {
  return Array.isArray(x) ? x[0] : x;
}

function nameVal(x) {
  return Array.isArray(x) ? x[1] : null;
}

const ESSLeavesPage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const { user } = useSelector((state) => state.auth);
  const [employeeId, setEmployeeId] = useState(null);
  const [leaveStats, setLeaveStats] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ typeId: "", typeName: "", from: "", to: "", reason: "" });

  const loadData = useCallback(async () => {
    try {
      const empRes = await get("/api/employees?limit=500");
      const allEmployees = empRes?.data || [];
      const currentEmp = allEmployees.find((e) => e.work_email === user?.email) || null;
      const empId = currentEmp?.id || currentEmp?.employee_id;
      setEmployeeId(empId);

      const [leavesRes] = await Promise.all([
        empId ? get(`/api/leave-requests?employee_id=${empId}&limit=50`) : { data: [] },
      ]);

      const leaveRecords = leavesRes?.data || [];

      const leaveTypeMap = {};
      if (Array.isArray(currentEmp?.leave_types)) {
        for (const lt of currentEmp.leave_types) {
          const id = idVal(lt.id != null ? lt.id : lt.value);
          const name = lt.name || lt.label;
          if (id != null && name) leaveTypeMap[id] = name;
        }
      }
      for (const l of leaveRecords) {
        if (l.holiday_status_id != null) {
          const id = idVal(l.holiday_status_id);
          const name = nameVal(l.holiday_status_id) || l.leave_type_name || l.leave_type;
          if (name) leaveTypeMap[id] = name;
        }
      }

      const types = Object.entries(leaveTypeMap).map(([id, name]) => ({ id, name }));
      if (types.length === 0) {
        const annual = intl.formatMessage({ id: "ess.leaves.annualLeave" });
        const sick = intl.formatMessage({ id: "ess.leaves.sickLeave" });
        leaveTypeMap[1] = annual;
        leaveTypeMap[2] = sick;
        types.push({ id: "1", name: annual });
        types.push({ id: "2", name: sick });
      }
      setLeaveTypes(types);

      const typeNameFor = (l) => {
        if (l.holiday_status_id == null) return "—";
        const id = idVal(l.holiday_status_id);
        const direct = nameVal(l.holiday_status_id) || l.leave_type_name || l.leave_type;
        return leaveTypeMap[id] || direct || String(id);
      };

      const history = leaveRecords.map(l => ({
        id: l.id,
        type: typeNameFor(l),
        from: fmtDate(l.date_from),
        to: fmtDate(l.date_to),
        days: intl.formatMessage({ id: "ess.leaves.daysShort" }, { count: l.number_of_days || 0 }),
        reason: l.name || "—",
        applied: fmtDate(l.date_from),
        status: STATE_MAP[l.state] || l.state,
      }));
      setLeaveHistory(history);

      const pendingCount = history.filter(h => h.status === "pending").length;
      const usedDays = history.filter(h => h.status === "approved").reduce((s, h) => {
        const days = parseInt(h.days) || 0;
        return s + days;
      }, 0);

      const annualBalance = currentEmp?.leave_balance_annual != null ? currentEmp.leave_balance_annual : (currentEmp?.annual_leave_remaining != null ? currentEmp.annual_leave_remaining : null);
      const sickBalance = currentEmp?.leave_balance_sick != null ? currentEmp.leave_balance_sick : (currentEmp?.sick_leave_remaining != null ? currentEmp.sick_leave_remaining : null);

      setLeaveBalances([
        { type: intl.formatMessage({ id: "ess.leaves.annualLeave" }), days: annualBalance != null ? Math.round(annualBalance) : "—", sub: intl.formatMessage({ id: "ess.leaves.remaining" }), used: usedDays },
        { type: intl.formatMessage({ id: "ess.leaves.sickLeave" }), days: sickBalance != null ? Math.round(sickBalance) : "—", sub: intl.formatMessage({ id: "ess.leaves.remaining" }), used: 0 },
      ]);

      setLeaveStats([
        { labelId: "ess.leaves.annualLeave", value: annualBalance != null ? String(Math.round(annualBalance)) : "—", sub: intl.formatMessage({ id: "ess.leaves.notConfigured" }), icon: Palmtree, bg: "bg-emerald-50/50 border-emerald-100", iconBg: "bg-emerald-500" },
        { labelId: "ess.leaves.sickLeave", value: sickBalance != null ? String(Math.round(sickBalance)) : "—", sub: intl.formatMessage({ id: "ess.leaves.notConfigured" }), icon: Stethoscope, bg: "bg-blue-50/50 border-blue-100", iconBg: "bg-blue-500" },
        { labelId: "ess.leaves.usedThisYear", value: intl.formatMessage({ id: "ess.leaves.daysShort" }, { count: usedDays }), sub: intl.formatMessage({ id: "ess.leaves.takenThisYear" }), icon: Calendar, bg: "bg-amber-50/50 border-amber-100", iconBg: "bg-amber-500" },
        { labelId: "ess.leaves.pending", value: String(pendingCount), sub: pendingCount ? intl.formatMessage({ id: "ess.leaves.waiting" }) : intl.formatMessage({ id: "ess.leaves.noWaiting" }), icon: Clock, bg: "bg-slate-50/50 border-slate-100", iconBg: "bg-slate-400" },
      ]);
    } catch (_) {}
    setLoaded(true);
  }, [user?.email, intl]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredHistory = useMemo(() => {
    if (activeFilter === "all") return leaveHistory;
    return leaveHistory.filter((row) => row.status === activeFilter);
  }, [activeFilter, leaveHistory]);

  const filters = useMemo(() => {
    const counts = { all: leaveHistory.length, pending: leaveHistory.filter((r) => r.status === "pending").length, approved: leaveHistory.filter((r) => r.status === "approved").length, refused: leaveHistory.filter((r) => r.status === "refused").length };
    return [
      { key: "all", label: intl.formatMessage({ id: "ess.leaves.filterAll" }), count: counts.all },
      { key: "pending", label: intl.formatMessage({ id: "ess.leaves.filterPending" }), count: counts.pending },
      { key: "approved", label: intl.formatMessage({ id: "ess.leaves.filterApproved" }), count: counts.approved },
      { key: "refused", label: intl.formatMessage({ id: "ess.leaves.filterRefused" }), count: counts.refused },
    ];
  }, [leaveHistory, intl]);

  const handleNewRequest = async () => {
    if (!form.from || !form.to || !form.reason) {
      addToast(intl.formatMessage({ id: "ess.common.fillAllFields" }), "error");
      return;
    }
    if (!form.typeId) {
      addToast(intl.formatMessage({ id: "ess.leaves.selectType" }), "error");
      return;
    }
    setSubmitting(true);
    try {
      const dateFrom = new Date(form.from);
      const dateTo = new Date(form.to);
      const days = Math.max(1, Math.ceil((dateTo - dateFrom) / (1000 * 60 * 60 * 24)) + 1);

      await post("/api/leave-requests", {
        employee_id: employeeId,
        holiday_status_id: parseInt(form.typeId) || 1,
        date_from: `${form.from}T00:00:00Z`,
        date_to: `${form.to}T00:00:00Z`,
        number_of_days: days,
        name: form.reason.trim(),
        private_name: form.reason.trim(),
      });

      setForm({ typeId: "", typeName: "", from: "", to: "", reason: "" });
      setModalOpen(false);
      addToast(intl.formatMessage({ id: "ess.leaves.submitSuccess" }), "success");
      window.location.reload();
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.common.submitError" }), "error");
    }
    setSubmitting(false);
  };

  if (!loaded) return <PageSkeleton />;

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {intl.formatMessage({ id: "ess.leaves.title" })}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {intl.formatMessage({ id: "ess.leaves.subtitle" })}
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-sm"
        >
          <Plus size={16} /> {intl.formatMessage({ id: "ess.leaves.newRequest" })}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {leaveStats.map(({ labelId, value, sub, icon: Icon, bg, iconBg }) => (
          <div key={labelId} className={`rounded-xl border p-5 ${bg} transition-all hover:shadow-md`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  {intl.formatMessage({ id: labelId })}
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                <p className="text-xs text-gray-500 mt-1">{sub}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0 ${iconBg}`}>
                <Icon size={20} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
          <h3 className="text-base font-bold text-gray-900 mb-4">
            {intl.formatMessage({ id: "ess.leaves.balances" })}
          </h3>
          <div className="divide-y divide-gray-100">
            {leaveBalances.map((lb) => (
              <div key={lb.type} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
                <div>
                  <p className="text-sm font-bold text-gray-900">{lb.type}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{lb.sub}</p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-emerald-600 leading-none">{lb.days}</p>
                  <p className="text-xs text-gray-400 mt-1">{intl.formatMessage({ id: "ess.leaves.daysUnit" })}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 transition-all hover:shadow-md">
          <h3 className="text-base font-bold text-gray-900 mb-4">
            {intl.formatMessage({ id: "ess.leaves.usageTitle" })}
          </h3>
          {leaveBalances.length > 0 ? (
            <div className="space-y-4">
              {leaveBalances.map((lb, i) => {
                  const maxVal = Math.max(...leaveBalances.map(b => Number(b.days) || 0), 1);
                  const pct = ((Number(lb.days) || 0) / maxVal) * 100;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-semibold text-gray-700">{lb.type}</span>
                        <span className="text-gray-500">{intl.formatMessage({ id: "ess.leaves.daysCount" }, { count: lb.days })}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div className="bg-emerald-500 h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="h-[280px] border border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
              <BarChart3 size={28} className="text-gray-300" />
              <span>{intl.formatMessage({ id: "ess.leaves.noBalanceData" })}</span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 border-b border-gray-100">
          <div className="flex items-center overflow-x-auto">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`flex items-center gap-2 px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  activeFilter === f.key
                    ? "border-emerald-500 text-emerald-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {f.label}
                <span className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                  {f.count}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.leaves.colNumber" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.leaves.colType" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.leaves.colFrom" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden sm:table-cell">{intl.formatMessage({ id: "ess.leaves.colTo" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.leaves.colDays" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden md:table-cell">{intl.formatMessage({ id: "ess.leaves.colReason" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden md:table-cell">{intl.formatMessage({ id: "ess.leaves.colApplied" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.leaves.colStatus" })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredHistory.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-gray-400">
                    {intl.formatMessage({ id: "ess.leaves.noResults" })}
                  </td>
                </tr>
              )}
              {filteredHistory.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5 font-semibold text-gray-900">{row.id}</td>
                  <td className="px-5 py-3.5 text-gray-700">{row.type}</td>
                  <td className="px-5 py-3.5 text-gray-600">{row.from}</td>
                  <td className="px-5 py-3.5 text-gray-600 hidden sm:table-cell">{row.to}</td>
                  <td className="px-5 py-3.5 text-gray-700">{row.days}</td>
                  <td className="px-5 py-3.5 text-gray-600 hidden md:table-cell">{row.reason}</td>
                  <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">{row.applied}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATE_STYLE[row.status] || "bg-gray-50 text-gray-600 border-gray-100"}`}>
                      {intl.formatMessage({ id: `ess.status.${row.status}` }) || row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={intl.formatMessage({ id: "ess.leaves.modalTitle" })}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.leaves.typeLabel" })}</label>
            <select
              value={form.typeId}
              onChange={(e) => {
                setForm({ ...form, typeId: e.target.value });
              }}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white"
            >
              <option value="">{intl.formatMessage({ id: "ess.common.select" })}</option>
              {leaveTypes.map((lt) => (
                <option key={lt.id} value={lt.id}>{lt.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.leaves.colFrom" })}</label>
              <input
                type="date"
                value={form.from}
                onChange={(e) => setForm({ ...form, from: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.leaves.colTo" })}</label>
              <input
                type="date"
                value={form.to}
                onChange={(e) => setForm({ ...form, to: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{intl.formatMessage({ id: "ess.leaves.reasonLabel" })}</label>
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none"
              placeholder={intl.formatMessage({ id: "ess.leaves.reasonPlaceholder" })}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setModalOpen(false)}
              className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              {intl.formatMessage({ id: "ess.common.cancel" })}
            </button>
            <button
              onClick={handleNewRequest}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin mr-2" /> {intl.formatMessage({ id: "ess.common.sending" })}</> : intl.formatMessage({ id: "ess.common.submit" })}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ESSLeavesPage;
